import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type Ref } from 'react'
import { HOVER_LABEL_OFFSET_ABOVE, HOVER_LABEL_OFFSET_BELOW } from '../../lib/chrome_config'
import type { HoverAbout } from '../../lib/hover'

export interface HoverLabelHandle {
  /**
   * Name the feature under the pointer, or clear the label with `null`. `about`
   * is the rest of what the map says about it (lib/hover.ts); without it the
   * label is the name alone, upright.
   */
  show: (name: string | null, about?: HoverAbout) => void
}

// How close the label may come to the viewport's edge.
const EDGE_MARGIN = 8

// How much ground round a name on the map the label keeps off: about half a
// two-word appellation name each way, and a line of it up and down.
const NAME_HALF_WIDTH = 56
const NAME_HALF_HEIGHT = 16

interface Shown {
  name: string
  about?: HoverAbout
}

/**
 * The name of whatever the pointer is over, as a tooltip AT the pointer — and
 * one line more, so that it adds something.
 *
 * It used to be a chip in the top-right nav, which had two costs. It was far
 * from where the reader was looking — the answer to "what is this?" appeared a
 * screen-width away from the thing — and because it rendered in the nav before
 * the links, each name pushed Journal / Wishlist / Sources sideways.
 *
 * **Both what it says and where it is live HERE, not in the layout, and that
 * is the point of the component.** The layout hands the map a stable callback
 * that calls `show` through a ref, so a change re-renders this one `<span>`
 * instead of the whole shell (which carries a hidden list of every country).
 * The position is never React state at all: a passive `pointermove` listener
 * writes a `transform` straight onto the node, so moving the mouse across the
 * map costs no render, no layout and no paint beyond the label's own layer.
 *
 * `show(null)` unmounts it in the same update, so the label cannot outlive the
 * feature it named.
 *
 * Hidden where the primary input cannot hover. A tap on a touch screen still
 * sends the compatibility `mousemove` MapLibre listens for, and with no
 * `mouseleave` to follow it the label would be left stranded under the
 * reader's thumb.
 *
 * WHAT IT SAYS. The name, and under it whatever the map can add that it has
 * not already lettered a few pixels away: "AVA in Santa Ynez Valley · 3
 * wineries", what the atlas holds for a country, how many producers a chip
 * stands for. A tooltip that only repeated the name was a paper chip covering
 * the neighbours to say what the map had just said.
 *
 * HOW IT IS SET. A paper chip, like every other label the chrome lays on the
 * plate. The name is in the map's own faces, by the map's own rule: an AREA —
 * an appellation — in the italic and the claret the map letters it in, a point
 * upright in ink. (It was upright for everything, because the chrome's
 * Fraunces is the upright cut and `italic` on it is the browser slanting the
 * wrong one. The italic is a face of its own now, `--font-area`, drawn from
 * the file the map has already fetched.) At a regular weight: semibold made
 * the chip the heaviest lettering on the page, over a map set in a book
 * weight.
 *
 * WHERE IT GOES. Above the pointer and centred on it, so the thing pointed at
 * stays visible under the pointer and the chip does not trail off to one side
 * over the neighbours. Higher still when that would land on the feature's OWN
 * name, where the map says it has one (`about.anchor`): hovering Napa Valley
 * put a chip reading "Napa Valley" on top of the map's "Napa Valley". Below
 * the pointer only when there is no room above.
 */
export default function HoverLabel({ ref }: { ref: Ref<HoverLabelHandle> }) {
  const [shown, setShown] = useState<Shown | null>(null)
  const label = useRef<HTMLSpanElement>(null)
  // Tracked even while nothing is shown, so the label appears at the pointer
  // rather than at wherever it last was.
  const pointer = useRef({ x: 0, y: 0 })
  // Measured once per name rather than per move: reading `offsetWidth` inside a
  // pointer handler would force a layout on every event.
  const size = useRef({ width: 0, height: 0 })
  // The name on the map to keep off, for the label now showing.
  const anchor = useRef<[number, number] | null>(null)

  useImperativeHandle(
    ref,
    () => ({ show: (name, about) => setShown(name === null ? null : { name, about }) }),
    [],
  )

  const place = useCallback(() => {
    const node = label.current
    if (!node) return

    const { x, y } = pointer.current
    const { width, height } = size.current

    // Centred on the pointer, and kept on the screen.
    const left = Math.min(Math.max(EDGE_MARGIN, x - width / 2), window.innerWidth - EDGE_MARGIN - width)

    const above = y - HOVER_LABEL_OFFSET_ABOVE - height
    const below = y + HOVER_LABEL_OFFSET_BELOW
    const name = anchor.current

    // Whether the chip, set above, would lie on the name the map has drawn.
    const onName =
      name !== null &&
      name[0] > left - NAME_HALF_WIDTH &&
      name[0] < left + width + NAME_HALF_WIDTH &&
      name[1] > above - NAME_HALF_HEIGHT &&
      name[1] < above + height + NAME_HALF_HEIGHT

    // On the name: further up, clear above it, so the chip is still over the
    // pointer and never under the hand. Only when there is no room up there
    // does it go below.
    const clear = onName && name ? name[1] - NAME_HALF_HEIGHT - height : above
    const fitsBelow = below + height <= window.innerHeight - EDGE_MARGIN
    const top = clear < EDGE_MARGIN && fitsBelow ? below : Math.max(EDGE_MARGIN, clear)

    node.style.transform = `translate3d(${Math.max(EDGE_MARGIN, left)}px, ${top}px, 0)`
  }, [])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY }
      place()
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    return () => window.removeEventListener('pointermove', onMove)
  }, [place])

  // Before paint, so a new name is never drawn for a frame at the old name's
  // position or width.
  useLayoutEffect(() => {
    const node = label.current
    anchor.current = shown?.about?.anchor ?? null
    if (!node) return

    size.current = { width: node.offsetWidth, height: node.offsetHeight }
    place()
  }, [shown, place])

  if (!shown) return null

  const area = shown.about?.area ?? false
  const detail = shown.about?.detail ?? null

  return (
    <span
      ref={label}
      data-testid="map-hover"
      className="pointer-events-none fixed left-0 top-0 z-40 flex max-w-[19rem] flex-col gap-0.5 rounded-xl bg-surface-raised px-3 py-1.5 shadow-chrome ring-1 ring-black/5 [@media(hover:none)]:hidden"
    >
      <span
        data-testid="map-hover-name"
        className={`truncate text-[15px] leading-tight ${
          area ? 'font-area italic text-accent' : 'font-display font-medium tracking-tight text-ink'
        }`}
      >
        {shown.name}
      </span>
      {detail && (
        <span data-testid="map-hover-detail" className="truncate text-[11px] leading-snug text-ink-soft">
          {detail}
        </span>
      )}
    </span>
  )
}
